import { memo, Suspense, useMemo } from 'react';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { Fallback, HStack, VStack } from '@cbhq/cds-web/layout';
import {
  AddLiquidityTokenPairSelector,
  AddLiquidityTokenPairSelectorLoading,
} from '@/components/AddLiquidity/AddLiquidityTokenPairSelector';
import {
  AddLiquidityPriceInputs,
  AddLiquidityPriceInputsLoading,
} from '@/components/AddLiquidity/AddLiquidityPriceInputs';
import {
  AddLiquidityPriceRangeControls,
  AddLiquidityPriceRangeControlsLoading,
} from '@/components/AddLiquidity/AddLiquidityPriceRangeControls';
import {
  AddLiquidityDepositInputs,
  AddLiquidityDepositInputsLoading,
} from '@/components/AddLiquidity/AddLiquidityDepositInputs';
import {
  AddLiquidityDepthChart,
  AddLiquidityDepthChartLoading,
} from '@/components/AddLiquidity/AddLiquidityDepthChart';
import { AddLiquidityUSDCRewardsBanner } from '@/components/AddLiquidity/AddLiquidityUSDCRewardsBanner';
import { useAddLiquidityState } from '@/components/AddLiquidity/AddLiquidityProvider';
import { isPositiveNumericInputValue } from '@/components/AddLiquidity/utils';
import { CurrencyField } from '@/types';

type AddLiquidityFormContentProps = {
  positionId: string | null;
  onContinue: () => void;
  onCancel: () => void;
};

/**
 * Form content for adding liquidity.
 * Contains all form inputs (token selector, price range, deposit amounts)
 * and Cancel/Continue buttons.
 *
 * This component is used within the AddLiquidity flow and will be
 * integrated into the FullscreenModal in a future PR.
 */
export const AddLiquidityFormContent = memo(function AddLiquidityFormContent({
  positionId,
  onContinue,
  onCancel,
}: AddLiquidityFormContentProps) {
  const {
    depositAmountsInput,
    accountBalances,
    positionTokens,
    liquidity,
    currencyDepositDisabled,
  } = useAddLiquidityState();

  // Disabled logic extracted from AddLiquidityConfirmButton
  const disabled = useMemo(() => {
    const insufficientBalance =
      Number(accountBalances[positionTokens[0].asset.displaySymbol].formatted) <
        Number(depositAmountsInput[0] ?? '') ||
      Number(accountBalances[positionTokens[1].asset.displaySymbol].formatted) <
        Number(depositAmountsInput[1] ?? '');

    // if deposit is disabled, we don't need to check if the input is valid
    const currency0DepositValid =
      currencyDepositDisabled[CurrencyField.CURRENCY_0] ||
      isPositiveNumericInputValue(depositAmountsInput[0]);
    const currency1DepositValid =
      currencyDepositDisabled[CurrencyField.CURRENCY_1] ||
      isPositiveNumericInputValue(depositAmountsInput[1]);

    // liquidity should always be a positive number
    const validLiquidity = liquidity > 0n;

    return (
      insufficientBalance || !currency0DepositValid || !currency1DepositValid || !validLiquidity
    );
  }, [accountBalances, positionTokens, depositAmountsInput, currencyDepositDisabled, liquidity]);

  return (
    <VStack gap={3}>
      <Suspense fallback={<AddLiquidityTokenPairSelectorLoading positionId={positionId} />}>
        <AddLiquidityTokenPairSelector positionId={positionId} />
      </Suspense>

      <Suspense fallback={<AddLiquidityPriceRangeControlsLoading positionId={positionId} />}>
        <AddLiquidityPriceRangeControls positionId={positionId} />
      </Suspense>

      <Suspense fallback={<AddLiquidityDepthChartLoading />}>
        <AddLiquidityDepthChart />
      </Suspense>

      <Suspense fallback={<AddLiquidityPriceInputsLoading />}>
        <AddLiquidityPriceInputs positionId={positionId} />
      </Suspense>

      <Suspense fallback={<AddLiquidityDepositInputsLoading />}>
        <AddLiquidityDepositInputs />
      </Suspense>

      <Suspense fallback={<Fallback width="100%" height={56} shape="squircle" />}>
        <AddLiquidityUSDCRewardsBanner />
      </Suspense>

      <HStack gap={2} width="100%">
        <Button
          as="button"
          variant="secondary"
          block
          onClick={onCancel}
          testID="add-liquidity-cancel-button"
        >
          Cancel
        </Button>
        <Button
          as="button"
          testID="add-liquidity-continue-button"
          block
          disabled={disabled}
          onClick={onContinue}
        >
          Continue
        </Button>
      </HStack>
    </VStack>
  );
});

/**
 * Loading state for AddLiquidityFormContent
 */
export const AddLiquidityFormContentLoading = memo(function AddLiquidityFormContentLoading({
  positionId,
}: {
  positionId: string | null;
}) {
  return (
    <VStack gap={3}>
      <AddLiquidityTokenPairSelectorLoading positionId={positionId} />
      <AddLiquidityPriceRangeControlsLoading positionId={positionId} />
      <AddLiquidityDepthChartLoading />
      <AddLiquidityPriceInputsLoading />
      <AddLiquidityDepositInputsLoading />
      <Fallback width="100%" height={56} shape="squircle" />
      <HStack gap={2} width="100%">
        <Fallback width="100%" height={48} shape="squircle" />
        <Fallback width="100%" height={48} shape="squircle" />
      </HStack>
    </VStack>
  );
});
